import { Check, Shield, Wallet, ArrowRight, ExternalLink, X, Clock } from 'lucide-react'
import { ChainIcon } from '@/components/ChainIcon'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useTheme } from '@/contexts/ThemeContext'
import { FONT_BODY, FONT_MONO } from '@/config/theme'
import type { ProgressStep } from '@/utils/steps'
import type { Chain } from '@/types'

interface StepRowProps {
  step: ProgressStep
  isLast: boolean
  sourceChain?: Chain | null
  destChain?: Chain | null
}

const ICON_SIZE = 18

export function StepRow({ step, isLast, sourceChain, destChain }: StepRowProps) {
  const c = useThemeColors()
  const { theme } = useTheme()
  const dark = theme === 'dark'

  const isPending = step.status === 'pending'
  const isActive = step.status === 'active'
  const isDone = step.status === 'done'
  const isError = step.status === 'error'
  /**
   * DEV-375 — the step was BROADCAST and we could not obtain a receipt.
   *
   * It renders as its own third outcome, never as a variant of `isError`: the icon well and label
   * stay in the neutral `c.tx` treatment (which `genericColor` already gives any non-pending,
   * non-active row, so nothing about the icon needed to change), the badge is a clock rather than
   * the red X, and the hardcoded "Failed — check explorer for details" line below is NOT printed.
   */
  const isUnknown = step.status === 'unknown'

  const genericColor = isPending ? c.mu : isActive ? c.ac : c.tx
  const chainFilter = isPending ? 'grayscale(1)' : 'none'
  const chainOpacity = isPending ? 0.45 : 1

  // Well background: neutral in all states, accent-tinted only when active
  const wellBg = isActive ? c.aB : c.iB

  // Build the step icon (always shown — never replaced by check/spinner)
  let icon: React.ReactNode
  switch (step.id) {
    case 'approve':
      icon = <Shield size={ICON_SIZE} style={{ color: genericColor, transition: 'color 0.25s' }} />
      break
    case 'sign':
      icon = <Wallet size={ICON_SIZE} style={{ color: genericColor, transition: 'color 0.25s' }} />
      break
    case 'source':
      icon = sourceChain ? (
        <div style={{ filter: chainFilter, opacity: chainOpacity, transition: 'filter 0.3s, opacity 0.3s', display: 'flex' }}>
          <ChainIcon chain={sourceChain} size={ICON_SIZE} />
        </div>
      ) : (
        <Shield size={ICON_SIZE} style={{ color: genericColor, transition: 'color 0.25s' }} />
      )
      break
    case 'bridge':
      icon = <ArrowRight size={ICON_SIZE} style={{ color: genericColor, transition: 'color 0.25s' }} />
      break
    case 'destination':
      icon = destChain ? (
        <div style={{ filter: chainFilter, opacity: chainOpacity, transition: 'filter 0.3s, opacity 0.3s', display: 'flex' }}>
          <ChainIcon chain={destChain} size={ICON_SIZE} />
        </div>
      ) : (
        <ArrowRight size={ICON_SIZE} style={{ color: genericColor, transition: 'color 0.25s' }} />
      )
      break
    case 'quote':
      icon = <Shield size={ICON_SIZE} style={{ color: genericColor, transition: 'color 0.25s' }} />
      break
    case 'confirm':
      icon = sourceChain ? (
        <div style={{ filter: chainFilter, opacity: chainOpacity, transition: 'filter 0.3s, opacity 0.3s', display: 'flex' }}>
          <ChainIcon chain={sourceChain} size={ICON_SIZE} />
        </div>
      ) : (
        <Check size={ICON_SIZE} style={{ color: genericColor, transition: 'color 0.25s' }} />
      )
      break
    default:
      icon = <Shield size={ICON_SIZE} style={{ color: genericColor, transition: 'color 0.25s' }} />
  }

  const badgeGreen = dark ? '#1DD881' : c.gr
  const badgeCheckColor = dark ? '#0A1520' : '#fff'

  return (
    <div style={{ display: 'flex', gap: 12, padding: '10px 0', alignItems: 'flex-start', fontFamily: FONT_BODY }}>
      {/* Left: 36×36 icon well with overlays */}
      <div style={{ position: 'relative', width: 36, height: 36, flexShrink: 0 }}>
        {/* Neutral well */}
        <div style={{
          width: 36, height: 36, borderRadius: '50%',
          background: wellBg,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'background 0.25s',
        }}>
          {icon}
        </div>

        {/* Active: SVG arc spinner around well */}
        {isActive && (
          <svg
            width={42} height={42}
            style={{ position: 'absolute', top: -3, left: -3, animation: 'spin 1.4s linear infinite' }}
          >
            <circle
              cx={21} cy={21} r={20}
              fill="none"
              stroke={c.ac}
              strokeWidth={2}
              strokeDasharray="36 90"
              strokeLinecap="round"
            />
          </svg>
        )}

        {/* Done badge: green circle with check */}
        {isDone && (
          <div style={{
            position: 'absolute', bottom: -3, right: -3,
            width: 19, height: 19, borderRadius: '50%',
            background: badgeGreen,
            border: `2px solid ${c.card}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            animation: 'checkBounce 0.28s ease-out both',
          }}>
            <Check size={11} strokeWidth={3.5} style={{ color: badgeCheckColor }} />
          </div>
        )}

        {/* Error badge: red circle with X */}
        {isError && (
          <div style={{
            position: 'absolute', bottom: -3, right: -3,
            width: 19, height: 19, borderRadius: '50%',
            background: c.eC,
            border: `2px solid ${c.card}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <X size={11} strokeWidth={3.5} style={{ color: '#fff' }} />
          </div>
        )}

        {/* DEV-375 unknown badge: a NEUTRAL clock, never the red X. The colour is the theme's warning
            tone (`c.wC`), which is deliberately NOT `c.eC` — a user scanning the card must not read
            "we could not confirm this" as "this failed". */}
        {isUnknown && (
          <div
            data-testid="step-unknown-badge"
            style={{
              position: 'absolute', bottom: -3, right: -3,
              width: 19, height: 19, borderRadius: '50%',
              background: c.wB,
              border: `2px solid ${c.card}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <Clock size={11} strokeWidth={3} style={{ color: c.wC }} />
          </div>
        )}

        {/* Done trail: animated green line below well */}
        {isDone && !isLast && (
          <div style={{
            position: 'absolute', left: '50%', top: '100%',
            width: 1.5, height: 24,
            background: badgeGreen,
            opacity: 0.55,
            transformOrigin: 'top',
            animation: 'railGrow 0.35s cubic-bezier(0.22, 1, 0.36, 1)',
          }} />
        )}
      </div>

      {/* Right: text column */}
      <div style={{ flex: 1, paddingBottom: isLast ? 0 : 4 }}>
        <div style={{
          fontSize: 14,
          fontWeight: isActive ? 600 : 500,
          color: isPending ? c.mu : c.tx,
        }}>
          {step.label}
        </div>
        {/* DEV-375: an 'unknown' row shows its OWN sublabel — which on the sign/source rows is
            `TX: 0x…`, the hash the user is being told to go and check. Before this the sublabel was
            active-only, so the one state that most needs to name a transaction showed none. */}
        {(step.status === 'active' || isUnknown) && step.sublabel && (
          <div style={{
            fontSize: 12, color: c.mu, marginTop: 2,
            fontFamily: step.txHash ? FONT_MONO : FONT_BODY,
          }}>
            {step.sublabel}
          </div>
        )}
        {step.explorerUrl && step.explorerUrl !== '#' && (
          <a
            href={step.explorerUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              fontSize: 12, color: c.ac, textDecoration: 'none',
              marginTop: 4,
            }}
          >
            View on explorer <ExternalLink size={10} />
          </a>
        )}
        {/* DEV-375 — gated on 'error' ALONE, and that is the load-bearing half of the fix. This
            literal is what the user actually saw on three beta transfers that had DELIVERED: the
            error string was never on screen, this hardcoded line was. Widening the error predicate
            without making 'unknown' skip this line would have changed nothing anybody could see. */}
        {isError && (
          <div style={{
            fontSize: 12, color: c.eC, marginTop: 4,
          }}>
            Failed — check explorer for details
          </div>
        )}
        {/* The unknown row's own line. It states the situation and points at the explorer link
            rendered just above; it must not assert a failure, a refund, or an elapsed duration.
            DEV-375 finding #6: gated on ALSO carrying a hash/link, not on `isUnknown` alone — with
            the flag OFF, `steps.ts` sends ALL THREE rows to `failStatus()`, so without this gate the
            quote/sign rows (which carry neither) printed the same "check the explorer" line with no
            link and no hash to check. It now prints once, on the row that actually has one. */}
        {isUnknown && (step.explorerUrl || step.txHash) && (
          <div style={{
            fontSize: 12, color: c.wC, marginTop: 4,
          }}>
            Couldn’t confirm on-chain — check the explorer before retrying
          </div>
        )}
      </div>
    </div>
  )
}
